import { v4 as UUIDv4 } from 'uuid';

import {
  Comparison,
  Dashboard,
  DashboardPayload,
  DashboardType,
  WidgetGroup,
} from 'app/providers/bridge-api/dashboard.service';
import { Template } from 'app/providers/dashboard-templates.service';

export interface DashboardInitializer {
  type: DashboardType;
  projectId: string;
  dataSourceId: string;

  initialize: (dashboard: Partial<Dashboard>) => Partial<Dashboard>;
}

class NoopDashboardInitalizer implements DashboardInitializer {
  type: DashboardType = 'traditional';

  constructor(
    public projectId,
    public dataSourceId
  ) {}

  initialize: (dashboard: Partial<Dashboard>) => Partial<Dashboard> = (dashboard) => dashboard;
}

class SurveyDashboardInitalizer implements DashboardInitializer {
  type: DashboardType = 'survey';

  constructor(
    public projectId,
    public dataSourceId
  ) {}

  initialize: (dashboard: Partial<Dashboard>) => Partial<Dashboard> = (dashboard) => {
    dashboard.payload.pages = [
      { title: 'Overview', id: 1 },
      { title: 'Taxonomy Drivers', id: 2 },
    ];

    dashboard.widgets.push({
      component: 'DashboardWidgetNotesComponent',
      payload: {
        projectId: this.projectId,
        page: 1,
        userSelectedDateRange: false,
        type: 'note',
        text: '',
      },
      id: UUIDv4(),
      x: 0,
      y: 0,
      cols: 4,
      rows: 8,
      title: '',
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        type: 'numericMetadata',
        dateInterval: 'day',
        graphType: 'spline',
        customFieldSelected: true,
        customItemColors: [],
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
        numericMetadataFields: [
          {
            scoring: 'sum',
            boxSize: '',
            color: {
              red: 0,
              green: 88,
              blue: 255,
              alpha: 1,
            },
            label: 'Documents',
            fieldId: 'docs',
          },
        ],
        legends: {
          Documents: true,
        },
        projectId: this.projectId,
        page: 1,
        sentimentScale: 3,
        userSelectedDateRange: false,
      },
      id: UUIDv4(),
      x: 4,
      y: 0,
      cols: 4,
      rows: 8,
      title: '',
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetBigNumberComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        aggregator: 'avg',
        analyticItem: 'documents',
        customFieldSelected: false,
        dateInterval: 'year',
        scoreRange: '1:10',
        scoreRangeTwo: '1:10',
        scoringStrategy: 'AVERAGE',
        scoringStrategyTwo: 'AVERAGE',
        sentimentScale: 3,
        type: 'bigNumber',
        userSelectedDateRange: false,
        projectId: this.projectId,
        page: 1,
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      id: UUIDv4(),
      x: 8,
      y: 0,
      cols: 4,
      rows: 8,
      title: '',
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'WidgetTaxonomyMapComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        userSelectedDateRange: false,
        analyticItem: 'taxonomy',
        type: 'taxonomyMap',
        legends: {
          'Series 1': true,
        },
        taxonomyPath: '',
        prevTaxonomyLevels: [],
        limit: 10,
        customItemColors: [],
        sentimentScale: 3,
        excludedValues: [],
        page: 1,
        projectId: this.projectId,
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      id: UUIDv4(),
      x: 0,
      y: 8,
      title: '',
      rows: 8,
      cols: 12,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'spline',
        userSelectedDateRange: false,
        aggregator: 'AVG',
        analyticItem: 'documents',
        type: 'sentimentScore',
        drilledDownOn: 'none',
        customFieldSelected: false,
        legends: {
          value: true,
        },
        entityTypeFilter: '',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 1,
        standardFieldSelected: false,
        selectedItems: [],
        dateInterval: 'month',
        projectId: this.projectId,
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      id: UUIDv4(),
      x: 0,
      y: 16,
      title: '',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'column',
        userSelectedDateRange: false,
        aggregator: 'total',
        analyticItem: 'taxonomy',
        type: 'sentimentPolarity',
        drilledDownOn: 'none',
        customFieldSelected: false,
        legends: {
          Neutral: true,
          Negative: true,
          Positive: true,
        },
        entityTypeFilter: 'Leaf Nodes',
        limit: 10,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 1,
        standardFieldSelected: false,
        selectedItems: [],
        projectId: this.projectId,
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      id: UUIDv4(),
      x: 6,
      y: 16,
      title: '',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'spline',
        userSelectedDateRange: false,
        aggregator: 'time',
        analyticItem: 'taxonomy',
        type: 'documentCount',
        drilledDownOn: 'none',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        legends: {},
        excludedValues: [],
        page: 1,
        standardFieldSelected: false,
        selectedItems: [],
        dateInterval: 'month',
        projectId: this.projectId,
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      id: UUIDv4(),
      x: 0,
      y: 24,
      title: '',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    // End of page 1

    const comparison: Comparison = {
      lastUsedDataField: 'createdAt',
      color: 'rgba(16,133,244,1)',
      lastUsedDataSource: this.dataSourceId,
      id: UUIDv4(),
      filters: [],
      page: 2,
      title: 'Month Over Month',
      type: 'date',
      widgets: [],
    };

    dashboard.comparisons = {
      [comparison.id]: comparison,
    };

    const positiveGroup: WidgetGroup = {
      orFilters: [],
      notFilters: [],
      editable: true,
      deletable: true,
      filters: {
        analyses: {
          not: [],
          or: [],
          and: [],
        },
        themes: {
          not: [],
          or: [],
          and: [],
        },
        core: {
          not: [],
          or: [],
          and: [],
        },
        metadata: {
          not: [],
          or: [],
          and: [],
        },
        entities: {
          not: [],
          or: [],
          and: [],
        },
        topics: {
          not: [],
          or: [],
          and: [],
        },
        dates: {
          not: [],
          or: [],
          and: [],
        },
        taxonomy: {
          not: [],
          or: [],
          and: [],
        },
        phrases: {
          not: [],
          or: [],
          and: [],
        },
        tags: {
          not: [],
          or: [],
          and: [],
        },
        sectionProperties: {
          not: [],
          or: [],
          and: [],
        },
      },
      title: 'Positive Leaf Nodes',
      widgets: [],
      version: 2,
      parentWidget: undefined,
      id: UUIDv4(),
      page: 2,
      parentFilters: {
        analyses: {
          not: [],
          or: [],
          and: [],
        },
        themes: {
          not: [],
          or: [],
          and: [],
        },
        core: {
          not: [],
          or: [],
          and: [],
        },
        metadata: {
          not: [],
          or: [],
          and: [],
        },
        entities: {
          not: [],
          or: [],
          and: [],
        },
        topics: {
          not: [],
          or: [],
          and: [],
        },
        dates: {
          not: [],
          or: [],
          and: [],
        },
        taxonomy: {
          not: [],
          or: [],
          and: [],
        },
        phrases: {
          not: [],
          or: [],
          and: [],
        },
        tags: {
          not: [],
          or: [],
          and: [],
        },
        sectionProperties: {
          not: [],
          or: [],
          and: [],
        },
      },
      parameters: {},
      isImpact: false,
      andFilters: [],
      filterCount: 0,
    };

    const negativeGroup: WidgetGroup = {
      orFilters: [],
      notFilters: [],
      editable: true,
      deletable: true,
      filters: {
        analyses: {
          not: [],
          or: [],
          and: [],
        },
        themes: {
          not: [],
          or: [],
          and: [],
        },
        core: {
          not: [],
          or: [],
          and: [],
        },
        metadata: {
          not: [],
          or: [],
          and: [],
        },
        entities: {
          not: [],
          or: [],
          and: [],
        },
        topics: {
          not: [],
          or: [],
          and: [],
        },
        dates: {
          not: [],
          or: [],
          and: [],
        },
        taxonomy: {
          not: [],
          or: [],
          and: [],
        },
        phrases: {
          not: [],
          or: [],
          and: [],
        },
        tags: {
          not: [],
          or: [],
          and: [],
        },
        sectionProperties: {
          not: [],
          or: [],
          and: [],
        },
      },
      title: 'Negative Leaf Nodes',
      widgets: [],
      version: 2,
      parentWidget: undefined,
      id: UUIDv4(),
      page: 2,
      parentFilters: {
        analyses: {
          not: [],
          or: [],
          and: [],
        },
        themes: {
          not: [],
          or: [],
          and: [],
        },
        core: {
          not: [],
          or: [],
          and: [],
        },
        metadata: {
          not: [],
          or: [],
          and: [],
        },
        entities: {
          not: [],
          or: [],
          and: [],
        },
        topics: {
          not: [],
          or: [],
          and: [],
        },
        dates: {
          not: [],
          or: [],
          and: [],
        },
        taxonomy: {
          not: [],
          or: [],
          and: [],
        },
        phrases: {
          not: [],
          or: [],
          and: [],
        },
        tags: {
          not: [],
          or: [],
          and: [],
        },
        sectionProperties: {
          not: [],
          or: [],
          and: [],
        },
      },
      parameters: {},
      isImpact: false,
      andFilters: [],
      filterCount: 0,
    };

    dashboard.widgetGroups = {
      [positiveGroup.id]: positiveGroup,
      [negativeGroup.id]: negativeGroup,
    };

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'spline',
        userSelectedDateRange: false,
        aggregator: 'percentage_time',
        analyticItem: 'taxonomy',
        filters: {
          analyses: {
            not: [],
            or: [],
            and: [],
          },
          themes: {
            not: [],
            or: [],
            and: [],
          },
          core: {
            not: [],
            or: [],
            and: [],
          },
          metadata: {
            not: [],
            or: [],
            and: [],
          },
          entities: {
            not: [],
            or: [],
            and: [],
          },
          topics: {
            not: [],
            or: [],
            and: [],
          },
          dates: {
            not: [],
            or: [],
            and: [],
          },
          taxonomy: {
            not: [],
            or: [
              {
                hidden: true,
                analyticItem: 'analyses.taxonomy.sentimentPolarity3',
                value: 'positive',
              },
            ],
            and: [],
          },
          phrases: {
            not: [],
            or: [],
            and: [],
          },
          tags: {
            not: [],
            or: [],
            and: [],
          },
        },
        legends: {},
        type: 'documentCount',
        drilledDownOn: 'none',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 2,
        standardFieldSelected: false,
        projectId: this.projectId,
        selectedItems: [],
        dateInterval: 'month',
      },
      id: UUIDv4(),
      x: 0,
      y: 0,
      title: 'Top Positive Leaf Nodes Over Time',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'spline',
        userSelectedDateRange: false,
        aggregator: 'percentage_time',
        analyticItem: 'taxonomy',
        filters: {
          analyses: {
            not: [],
            or: [],
            and: [],
          },
          themes: {
            not: [],
            or: [],
            and: [],
          },
          core: {
            not: [],
            or: [],
            and: [],
          },
          metadata: {
            not: [],
            or: [],
            and: [],
          },
          entities: {
            not: [],
            or: [],
            and: [],
          },
          topics: {
            not: [],
            or: [],
            and: [],
          },
          dates: {
            not: [],
            or: [],
            and: [],
          },
          taxonomy: {
            not: [],
            or: [
              {
                hidden: true,
                analyticItem: 'analyses.taxonomy.sentimentPolarity3',
                value: 'negative',
              },
            ],
            and: [],
          },
          phrases: {
            not: [],
            or: [],
            and: [],
          },
          tags: {
            not: [],
            or: [],
            and: [],
          },
        },
        legends: {},
        type: 'documentCount',
        drilledDownOn: 'none',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 2,
        standardFieldSelected: false,
        projectId: this.projectId,
        selectedItems: [],
        dateInterval: 'month',
      },
      id: UUIDv4(),
      x: 6,
      y: 0,
      title: 'Top Negative Leaf Nodes Over Time',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    const taxonomyTopPositiveWidget = {
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'column',
        comparison: comparison.id,
        userSelectedDateRange: false,
        aggregator: 'percentage',
        analyticItem: 'taxonomy',
        filters: {
          analyses: {
            not: [],
            or: [],
            and: [],
          },
          themes: {
            not: [],
            or: [],
            and: [],
          },
          core: {
            not: [],
            or: [],
            and: [],
          },
          metadata: {
            not: [],
            or: [],
            and: [],
          },
          entities: {
            not: [],
            or: [],
            and: [],
          },
          topics: {
            not: [],
            or: [],
            and: [],
          },
          dates: {
            not: [],
            or: [],
            and: [],
          },
          taxonomy: {
            not: [],
            or: [
              {
                hidden: true,
                analyticItem: 'analyses.taxonomy.sentimentPolarity3',
                value: 'positive',
              },
            ],
            and: [],
          },
          phrases: {
            not: [],
            or: [],
            and: [],
          },
          tags: {
            not: [],
            or: [],
            and: [],
          },
        },
        legends: { 'Series 1': true },
        type: 'documentCount',
        drilledDownOn: 'none',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 2,
        standardFieldSelected: false,
        projectId: this.projectId,
        selectedItems: [],
      },
      id: UUIDv4(),
      x: 0,
      y: 8,
      title: 'Taxonomy: Top Positive Leaf Nodes',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    };

    dashboard.widgets.push(taxonomyTopPositiveWidget);

    comparison.widgets.push(taxonomyTopPositiveWidget.id);
    positiveGroup.parentWidget = taxonomyTopPositiveWidget.id;

    const taxonomyTopNegativeWidget = {
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'column',
        comparison: comparison.id,
        userSelectedDateRange: false,
        aggregator: 'percentage',
        analyticItem: 'taxonomy',
        filters: {
          analyses: {
            not: [],
            or: [],
            and: [],
          },
          themes: {
            not: [],
            or: [],
            and: [],
          },
          core: {
            not: [],
            or: [],
            and: [],
          },
          metadata: {
            not: [],
            or: [],
            and: [],
          },
          entities: {
            not: [],
            or: [],
            and: [],
          },
          topics: {
            not: [],
            or: [],
            and: [],
          },
          dates: {
            not: [],
            or: [],
            and: [],
          },
          taxonomy: {
            not: [],
            or: [
              {
                hidden: true,
                analyticItem: 'analyses.taxonomy.sentimentPolarity3',
                value: 'negative',
              },
            ],
            and: [],
          },
          phrases: {
            not: [],
            or: [],
            and: [],
          },
          tags: {
            not: [],
            or: [],
            and: [],
          },
        },
        legends: { 'Series 1': true },
        type: 'documentCount',
        drilledDownOn: 'none',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        isDrilledDown: false,
        excludedValues: [],
        page: 2,
        standardFieldSelected: false,
        projectId: this.projectId,
        selectedItems: [],
      },
      id: UUIDv4(),
      x: 6,
      y: 8,
      title: 'Taxonomy: Top Negative Leaf Nodes',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    };

    dashboard.widgets.push(taxonomyTopNegativeWidget);

    comparison.widgets.push(taxonomyTopNegativeWidget.id);
    negativeGroup.parentWidget = taxonomyTopNegativeWidget.id;

    const positiveLeafSentimentPolarityWidget = {
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'column',
        userSelectedDateRange: false,
        aggregator: 'percentage',
        analyticItem: 'phrases',
        type: 'sentimentPolarity',
        legends: {
          Neutral: true,
          Negative: true,
          Positive: true,
        },
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        excludedValues: [],
        page: 2,
        projectId: this.projectId,
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      x: 0,
      y: 16,
      id: UUIDv4(),
      title: '',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    };

    dashboard.widgets.push(positiveLeafSentimentPolarityWidget);

    positiveGroup.widgets.push(positiveLeafSentimentPolarityWidget.id);

    const negativeLeafSentimentPolarityWidget = {
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        graphType: 'column',
        userSelectedDateRange: false,
        aggregator: 'percentage',
        analyticItem: 'phrases',
        type: 'sentimentPolarity',
        legends: {
          Neutral: true,
          Negative: true,
          Positive: true,
        },
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        excludedValues: [],
        page: 2,
        projectId: this.projectId,
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      x: 6,
      y: 16,
      id: UUIDv4(),
      title: '',
      rows: 8,
      cols: 6,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    };

    dashboard.widgets.push(negativeLeafSentimentPolarityWidget);

    negativeGroup.widgets.push(negativeLeafSentimentPolarityWidget.id);

    dashboard.widgets.push({
      component: 'DashboardWidgetChartComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        userSelectedDateRange: false,
        analyticItem: 'phrases',
        type: 'wordcloud',
        orderBySentiment: false,
        limit: 20,
        customItemColors: [],
        sentimentScale: 3,
        excludedValues: [],
        page: 2,
        projectId: this.projectId,
        selectedItems: [],
        legends: { Count: true },
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      x: 0,
      y: 24,
      id: UUIDv4(),
      title: '',
      rows: 8,
      cols: 4,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetDocumentListComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        userSelectedDateRange: false,
        sentimentScale: 3,
        excludedValues: [],
        page: 2,
        type: 'documents',
        projectId: this.projectId,
        selectedItems: [],
        isCardView: false,
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      x: 4,
      y: 24,
      id: UUIDv4(),
      title: '',
      rows: 8,
      cols: 8,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    dashboard.widgets.push({
      component: 'DashboardWidgetCrosstabComponent',
      dataSourceId: this.dataSourceId,
      payload: {
        columnCustomFieldSelected: false,
        columnAnalyticItem: 'phrases',
        columnSelectedItems: [],
        graphType: 'crosstab',
        userSelectedDateRange: false,
        columnEntityTypeFilter: '',
        display: {
          heatmap: true,
        },
        columnStandardFieldSelected: false,
        analyticItem: 'taxonomy',
        type: 'crosstab',
        customFieldSelected: false,
        entityTypeFilter: 'Leaf Nodes',
        limit: 20,
        sentimentScale: 3,
        excludedValues: [],
        page: 2,
        standardFieldSelected: false,
        projectId: this.projectId,
        selectedItems: [],
        filters: {
          analyses: {
            and: [],
            not: [],
            or: [],
          },
          core: {
            and: [],
            not: [],
            or: [],
          },
          dates: {
            and: [],
            not: [],
            or: [],
          },
          entities: {
            and: [],
            not: [],
            or: [],
          },
          metadata: {
            and: [],
            not: [],
            or: [],
          },
          phrases: {
            and: [],
            not: [],
            or: [],
          },
          tags: {
            and: [],
            not: [],
            or: [],
          },
          taxonomy: {
            and: [],
            not: [],
            or: [],
          },
          themes: {
            and: [],
            not: [],
            or: [],
          },
          topics: {
            and: [],
            not: [],
            or: [],
          },
        },
      },
      x: 0,
      y: 32,
      id: UUIDv4(),
      title: '',
      rows: 8,
      cols: 12,
      properties: {
        is_locked: false,
        as_document_list: false,
        as_data_table: false,
      },
    });

    return dashboard;
  };
}

class TemplateDashboardInitalizer implements DashboardInitializer {
  type: string;

  constructor(
    public projectId: string,
    public dataSourceId: string,
    public template: Template
  ) {}

  initialize: (dashboard: Partial<Dashboard>) => Partial<Dashboard> = (dashboard) => {
    const result = this.createDashboard(dashboard);

    this.updateWidgetIdsOnDashboard(result);

    return result;
  };

  private createDashboard(dashboard: Partial<Dashboard>): Dashboard {
    const templateDashboard = this.getTemplateDashboard();

    const newDashboard: Partial<Dashboard> = {
      id: undefined,
      creator: undefined,
      project_id: this.projectId,
      user_id: undefined,
      title: dashboard.title,
      modified_at_date: undefined,
      created_at_date: undefined,
      account_id: undefined,
      last_accessed_date: undefined,
    };

    const newPayload: Partial<DashboardPayload> = {
      collectionName: dashboard.payload.collectionName,
      analysisName: dashboard.payload.analysisName,
      notes: dashboard.payload.notes,
      sentimentScale: dashboard.payload.sentimentScale,
      sentimentDisplay: dashboard.payload.sentimentDisplay,
      type: this.template.dashboardType,
      timezone: dashboard.payload.timezone,
      timezoneOffset: dashboard.payload.timezoneOffset,
      lastDataSourceId: dashboard.payload.lastDataSourceId,
    };

    const result = { ...templateDashboard, ...newDashboard };
    result.payload = { ...result.payload, ...newPayload };

    return result;
  }

  private getTemplateDashboard(): Dashboard {
    let templateJson = this.template.templateJson;

    if (this.template.datasourceSlots?.length) {
      const regex = new RegExp(this.template.datasourceSlots[0].token, 'g');
      templateJson = templateJson.replace(regex, this.dataSourceId);
    }

    return JSON.parse(templateJson);
  }

  private updateWidgetIdsOnDashboard(dashboard: Dashboard): void {
    const newWidgetIdMap = new Map<string, string>();

    dashboard.widgets.forEach((w) => {
      w.payload.projectId = this.projectId;

      const newId = UUIDv4();

      newWidgetIdMap.set(w.id, newId);

      w.id = newId;
    });

    const updateWgOrComp = (object: {
      [key: string]: WidgetGroup | Comparison;
    }): Map<string, string> => {
      const newIdMap = new Map<string, string>();

      Object.keys(object).forEach((k) => {
        const item = object[k];

        item.widgets = item.widgets.map((w) => newWidgetIdMap.get(w));

        delete object[k];

        const newId = UUIDv4();

        newIdMap.set(item.id, newId);

        object[newId] = item;
        item.id = newId;
      });

      return newIdMap;
    };

    updateWgOrComp(dashboard.widgetGroups);
    const newCompIdMap = updateWgOrComp(dashboard.comparisons);

    dashboard.widgets
      .filter((w) => !!w.payload.comparison)
      .forEach((w) => (w.payload.comparison = newCompIdMap.get(w.payload.comparison)));
  }
}

export function createInitializer(
  type: DashboardType,
  projectId: string,
  dataSourceId: string,
  template?: Template
): DashboardInitializer {
  switch (type) {
    case 'traditional':
      return new NoopDashboardInitalizer(projectId, dataSourceId);
    case 'survey':
      return new SurveyDashboardInitalizer(projectId, dataSourceId);
    default:
      return new TemplateDashboardInitalizer(projectId, dataSourceId, template);
  }
}
